import "../shared/IntakeForm.scss";

import Button from "components/Button/Button";
import { ControlledInput } from "components/Card/ControlledInput";
import KaboodleToaster from "components/common/KaboodleToaster";
import { TextInput } from "components/inputs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import type { InitiatePasswordResetRequest } from "services/intake-api";
import { useInitiatePasswordResetMutation } from "services/intake-api";

import IntakeHeader from "../components/HeaderIntake";
import { useOrganizationSlug } from "../hooks/useOrganizationSlug";
import { logToSentry, getSessionId } from "utils/sentry";

const ForgotPasswordIntake = () => {
  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InitiatePasswordResetRequest>({
    defaultValues: {
      email: "",
    },
  });

  const [initiatePasswordReset, { isLoading }] =
    useInitiatePasswordResetMutation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const organizationSlug = useOrganizationSlug();

  const handleFieldChange = async () => Promise.resolve();
  const uiSessionId = getSessionId();
  useEffect(() => {
    logToSentry(`Intake forgot password page`, "info", { uiSessionId });
  }, [uiSessionId]);

  const onSubmit = async (data: InitiatePasswordResetRequest) => {
    const uiSessionId = getSessionId();
    try {
      await initiatePasswordReset(data).unwrap();
      setIsSubmitted(true);
      setError(null);
      logToSentry(`Intake password reset initiated`, "info", {
        uiSessionId,
        resetEmail: data.email,
      });
    } catch (err: unknown) {
      if (err && typeof err === "object" && "data" in err) {
        const errorData = (err as { data: { error?: string } }).data;
        setError(
          errorData.error || "Failed to send reset email. Please try again.",
        );
      } else {
        setError("Failed to send reset email. Please try again.");
      }
      logToSentry(`Intake password reset failed`, "error", {
        uiSessionId,
        resetEmail: data.email,
      });
      console.error(err);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <IntakeHeader />
        <div className="intake-form-page">
          <div className="intake-card">
            <h2 className="page-title">Check Your Email</h2>
            <p className="page-subtitle">
              We've sent password reset instructions to your email address.
              Please check your inbox and follow the instructions to reset your
              password.
            </p>
            <p className="page-subtitle">
              If you don't see the email, please check your spam folder.
            </p>

            <div className="prompt-message">
              Remember your password?{" "}
              <Link
                to={`/intake/${organizationSlug || "allan-koba"}/auth/login`}
                className="prompt-link"
              >
                Back to login
              </Link>
            </div>
          </div>
        </div>
        <KaboodleToaster.ToastContainer />
      </>
    );
  }

  return (
    <>
      <IntakeHeader />
      <div className="intake-form-page">
        <div className="intake-card">
          <h2 className="page-title">Forgot Your Password?</h2>
          <p className="page-subtitle">
            Enter your email address and we will send you instructions to reset
            your password.
          </p>

          <form onSubmit={handleFormSubmit(onSubmit)}>
            <ControlledInput
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Please enter a valid email address",
                },
              }}
              control={control}
              name="email"
              label="Email address"
              errors={errors}
              maxLength={50}
              onSave={handleFieldChange}
              componentType={TextInput}
            />

            {error && <div className="intake-form-page--Error">{error}</div>}

            <Button
              onClick={handleFormSubmit(onSubmit)}
              type="submit"
              className="button allan-koba"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? "Sending..." : "Continue"}
            </Button>
          </form>

          <div className="prompt-message">
            Remember your password?{" "}
            <Link
              to={`/intake/${organizationSlug || "allan-koba"}/auth/login`}
              className="prompt-link"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
      <KaboodleToaster.ToastContainer />
    </>
  );
};

export default ForgotPasswordIntake;
